import type { PlayerEngineController } from "./controller";
import type { PlayerAdEngineController } from "./adController";
import type { PlayerQueueType } from "../types";

/**
 * PlayerRuntime acts as the bridge between the Zustand store and the native audio engines.
 *
 * With the new declarative architecture:
 * - play/pause: Handled by setting userIntent in the store. The engine reacts declaratively.
 * - seek: Still requires direct engine interaction.
 * - ads: Still require direct engine control.
 */
export class PlayerRuntime {
  private engines: Record<PlayerQueueType, PlayerEngineController | null> = {
    foryou: null,
    playlist: null,
  };
  private adEngine: PlayerAdEngineController | null = null;

  setRuntimeEngine(
    queueType: PlayerQueueType,
    engine: PlayerEngineController | null,
  ) {
    this.engines[queueType] = engine;
  }

  getRuntimeEngine(queueType: PlayerQueueType) {
    return this.engines[queueType];
  }

  setRuntimeAdEngine(engine: PlayerAdEngineController | null) {
    this.adEngine = engine;
  }

  getRuntimeAdEngine() {
    return this.adEngine;
  }

  dispose() {
    this.engines.foryou = null;
    this.engines.playlist = null;
    this.adEngine = null;
  }

  /**
   * Seek to a specific position in the current track.
   * This still requires direct engine interaction.
   */
  public async seek(
    queueType: PlayerQueueType,
    positionSeconds: number,
  ): Promise<void> {
    console.audio.log("PlayerRuntime: seek", { queueType, positionSeconds });
    const engine = this.getRuntimeEngine(queueType);
    if (engine) {
      await engine.seekTo(positionSeconds);
    } else {
      console.audio.warn(
        "PlayerRuntime: seek failed - no engine for queue type",
        { queueType },
      );
    }
  }

  // Ad Engine control (ads still need direct control)
  public async playAd(): Promise<void> {
    console.audio.log("PlayerRuntime: playAd");
    const engine = this.getRuntimeAdEngine();
    if (engine) {
      await engine.play();
    } else {
      console.audio.warn("PlayerRuntime: playAd failed - no ad engine");
    }
  }

  public async pauseAd(): Promise<void> {
    console.audio.log("PlayerRuntime: pauseAd");
    const engine = this.getRuntimeAdEngine();
    if (engine) {
      await engine.pause();
    } else {
      console.audio.warn("PlayerRuntime: pauseAd failed - no ad engine");
    }
  }
}
